import { AzureAuthenticator } from 'tis-azure-auth';
import { AzureAdClient, ApiGateway } from '../config/config'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import EventBus from '../utils/EventBus'

const EMAIL_POPUP_SCOPES = ['openid', 'profile', 'offline_access', 'User.Read', 'Mail.Send']

let Authenticator = undefined
export const getAuthenticator = () => {
  if ( !Authenticator ) {
    Authenticator = new AzureAuthenticator({azureClientId: AzureAdClient.info.clientId, azureTenantId: AzureAdClient.info.tenantId})
  }
  return Authenticator
}

let token = undefined
export const getToken = async (_cacheOnly = false) => {
  if (_cacheOnly) return token
  
  if (token) {
    // to reduce traffic ensure token is a good distance from expiration but not too fresh
    const expirationTime = token.idTokenClaims?.exp ? new Date(token.idTokenClaims.exp * 1000).getTime() : 0
    const currentTime = new Date().getTime()
    if ((expirationTime - currentTime) / 1000 / 60 < 30) {
      token = undefined
    }
  }
  if (!token) {
    const auth = getAuthenticator()
    try {
      token = await auth.authenticate()
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        console.error(`InteractionRequiredAuthError caught and clearing session for retry`)
        sessionStorage.clear()
        token = await getToken()
      } else throw (e)
    }
  }
  EventBus.emit('token', token)
  return token
}

export const getRequestConfig = async (options = {}) => {
  const {
    includeApimSubscriptionKey = false,
  } = options
  const token = await getToken()
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token.idToken}`,
    'x-api-key': ApiGateway.apiKey
  }


  if (includeApimSubscriptionKey) {
    headers['Ocp-Apim-Subscription-Key'] = ApiGateway.apiKey
  }

  return {
    headers
  }
}

export function getHostUrl() {
  return `${[window.location.protocol,'//',
  window.location.hostname,
  window.location.hostname === 'localhost' ? [':',window.location.port].join('') : ''].join('')}`
}

export function logout() {
  getAuthenticator().logout()
}

let emailMsalClient = undefined

const getEmailMsalClient = () => {
  if (!emailMsalClient) {
    emailMsalClient = new PublicClientApplication({
      auth: {
        clientId: AzureAdClient.info.clientId,
        authority: `https://login.microsoftonline.com/${AzureAdClient.info.tenantId}`,
        redirectUri: getHostUrl(),
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    })
  }
  return emailMsalClient
}

export const getEmailPopupAccessToken = async (options = {}) => {
  // This function is intentionally silent-only by default to avoid
  // interrupting or changing frontend UX. It will not trigger popups.
  const { interactive = false, forceNetwork = false } = options
  const msalClient = getEmailMsalClient()

  if (typeof msalClient.initialize === 'function') {
    await msalClient.initialize()
  }

  // Cached ID token from our primary authenticator (if present)
  const cachedToken = await getToken(true)

  let account = msalClient.getActiveAccount()
  if (!account) {
    const accounts = msalClient.getAllAccounts()
    account =
      accounts.find((a) => a.tenantId === AzureAdClient.info.tenantId) ||
      accounts[0]
    if (account) {
      msalClient.setActiveAccount(account)
    }
  }

  // If MSAL has no active account, try to derive one from our cached ID token
  // rather than calling `ssoSilent()` (which uses hidden iframes and often fails
  // in modern browsers due to third-party cookie restrictions).
  if (!account) {
    const cachedToken = await getToken(true)
    const idClaims = cachedToken?.idTokenClaims
    if (idClaims) {
      const oid = idClaims.oid || idClaims.sub
      const tid = idClaims.tid || AzureAdClient.info.tenantId
      const username =
        idClaims.preferred_username || idClaims.upn || idClaims.email || idClaims.mail
      if (oid) {
        const derivedAccount = {
          homeAccountId: `${oid}.${tid}`,
          environment: 'login.microsoftonline.com',
          tenantId: tid,
          username,
          localAccountId: oid,
          idTokenClaims: idClaims,
        }
        try {
          msalClient.setActiveAccount(derivedAccount)
          account = derivedAccount
        } catch (err) {
          // If setting the account fails, fall back to interactive behavior below
          account = undefined
        }
      }
    }
    // If we still don't have an account and the caller doesn't allow interaction,
    // bail out early instead of attempting hidden-iframe SSO.
    if (!account && !interactive) {
      return undefined
    }
  }

  if (account) {
    try {
      const silentResponse = await msalClient.acquireTokenSilent({
        account,
        scopes: EMAIL_POPUP_SCOPES,
        forceRefresh: forceNetwork,
      })
      return silentResponse?.accessToken
    } catch (err) {
      // Do not attempt interactive flows. Log and return undefined so the
      // calling code can continue without UI disruption.
      console.warn('Email token silent acquisition failed:', err)
      return undefined
    }
  }

  // No account available to attempt silent acquisition.
  return undefined
}
